import { useState, useCallback, useEffect } from "react"
import { useSelector } from "react-redux"
import { useDebounce } from "hooks/useDebounce"
import { searchTripsSelector, searchTripsLoadingSelector } from "store/selectors"

export const useExperienceSearch = ({ pageSize = 10, onSearch }) => {
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("")
  const [confirmedSearchQuery, setConfirmedSearchQuery] = useState("")
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [showSearchResults, setShowSearchResults] = useState(false)
  
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  
  // Redux selectors
  const searchTrips = useSelector(searchTripsSelector)
  const searchLoading = useSelector(searchTripsLoadingSelector)
  
  // Search logic
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value
      setSearchQuery(value)
      onSearch?.(value)

      if (value.trim()) {
        setShowSearchResults(true)
      } else {
        setShowSearchResults(false)
      }

      if (!value.trim() || (selectedTrip && value.trim() !== selectedTrip.title)) {
        setSelectedTrip(null)
        setConfirmedSearchQuery("")
      }
    },
    [selectedTrip, onSearch]
  )

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && debouncedSearchQuery && debouncedSearchQuery.trim().length >= 3 && !searchLoading) {
        e.preventDefault()
        setConfirmedSearchQuery(debouncedSearchQuery.trim())
        setShowSuggestions(false)
        setSelectedTrip(null)
      }
    },
    [debouncedSearchQuery, searchLoading]
  )

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setConfirmedSearchQuery("")
    setSelectedTrip(null)
    setShowSuggestions(false)
    setShowSearchResults(false)
    onSearch?.("")
  }, [onSearch])

  const handleTripClick = useCallback(
    (trip) => {
      setSelectedTrip(trip)
      setSearchQuery(trip.title)
      setConfirmedSearchQuery(trip.title)
      setShowSuggestions(false)
      // Close the suggestions dropdown once a result is picked — otherwise it
      // keeps rendering off showSearchResults (still true) over the main list.
      setShowSearchResults(false)
    },
    []
  )

  // Effects for search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setShowSuggestions(false)
    }
  }, [searchQuery])

  return {
    // State
    searchQuery,
    confirmedSearchQuery,
    selectedTrip,
    showSuggestions,
    showSearchResults,
    debouncedSearchQuery,
    searchTrips,
    searchLoading,
    
    // Handlers
    handleSearchChange,
    handleSearchKeyDown,
    handleClearSearch,
    handleTripClick,
    setSearchQuery,
    setConfirmedSearchQuery,
    setSelectedTrip,
    setShowSuggestions,
    setShowSearchResults
  }
}
